import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHelmet from "./shared/SEOHelmet";
import SearchForm from "./SearchForm";
import { ZIP_PATTERNS, API } from "../constants";
import postalCodeHelper from "../Utils/postalCodeHelper";
import { clearAllOnLoad } from "../services/cacheService";

// Video URLs - served from Netlify CDN via public/videos/
const VIDEO_URLS = [
  "/videos/1CompareMechanicHome.mp4",
  "/videos/2CompareMechanicHome.mp4",
  "/videos/3CompareMechanicHome.mp4",
  "/videos/4CompareMechanicHome.mp4",
  "/videos/5CompareMechanicHome.mp4",
  "/videos/6CompareMechanicHome.mp4",
  "/videos/7CompareMechanicHome.mp4",
  "/videos/8CompareMechanicHome.mp4",
  "/videos/9CompareMechanicHome.mp4",
  "/videos/10CompareMechanicHome.mp4",
];

// Minimum viewport width (px) at which the hero video is visible in CSS.
// Must match the `@media (min-width: 768px)` breakpoint in App.css for `.home-video`.
// Below this width the video element is not rendered at all, so the browser
// never downloads the MP4 files (CSS `display: none` does NOT prevent download).
const VIDEO_MIN_VIEWPORT_WIDTH = 768;

// Performance: Check if videos should be loaded based on user preferences, connection, and viewport
const shouldLoadVideos = () => {
  // Check for reduced motion preference (accessibility)
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) {
    return false;
  }

  // Skip on mobile viewports — CSS hides the video below 768px, but the browser
  // would still download it if rendered. Gate rendering to save ~2.8 MB on mobile.
  if (typeof window !== "undefined" && window.innerWidth < VIDEO_MIN_VIEWPORT_WIDTH) {
    return false;
  }

  // Check connection speed if available (Network Information API)
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (connection) {
    // Skip on save-data mode
    if (connection.saveData) return false;
    // Skip on very slow connections
    if (connection.effectiveType === "slow-2g" || connection.effectiveType === "2g") return false;
  }

  return true;
};

function Home() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [zip, setZip] = useState("");
  const [error, setError] = useState("");
  const [locating, setLocating] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [nextVideoIndex, setNextVideoIndex] = useState(1);
  const [showingCurrent, setShowingCurrent] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  const video1Ref = useRef(null);
  const video2Ref = useRef(null);

  // Performance: Only load videos if viewport/connection allows.
  // Re-evaluated on resize so tablet orientation changes toggle the video correctly.
  const [videosEnabled, setVideosEnabled] = useState(() => shouldLoadVideos());

  useEffect(() => {
    const handleResize = () => {
      setVideosEnabled((prev) => {
        const next = shouldLoadVideos();
        return prev === next ? prev : next;
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Clear application caches on landing page load to prevent stale data issues
  useEffect(() => {
    clearAllOnLoad();
  }, []);

  // Load previously saved postal code on mount only (removed zip dependency to prevent re-renders)
  useEffect(() => {
    const savedPostalCode = postalCodeHelper.getPostalCode();
    if (savedPostalCode) {
      setZip(savedPostalCode);
    }
  }, []); // Empty dependency - only run once on mount

  const formatPostalCode = (value) => {
    // Convert to uppercase
    const upper = value.toUpperCase();

    // Check if it looks like a Canadian postal code (starts with a letter)
    const startsWithLetter = /^[A-Z]/.test(upper);

    if (startsWithLetter) {
      // Canadian format: A1A 1A1 - remove spaces, limit to 6, add space after 3rd char
      const cleaned = upper.replace(/\s/g, "").slice(0, 6);
      if (cleaned.length > 3) {
        return cleaned.slice(0, 3) + " " + cleaned.slice(3);
      }
      return cleaned;
    } else {
      // US/numeric format: preserve digits and hyphen, limit appropriately
      // US zip: 12345 or 12345-6789
      const cleaned = upper.replace(/[^\d-]/g, "");
      // Allow up to 10 chars for US zip+4 format (12345-6789)
      return cleaned.slice(0, 10);
    }
  };

  const handleZipChange = (value) => {
    setZip(formatPostalCode(value));
  };

  const validateZip = (value) => {
    return ZIP_PATTERNS.some((regex) => regex.test(value.trim()));
  };

  const handleUseMyLocation = async () => {
    setError("");
    setLocating(true);
    if (!navigator.geolocation) {
      setError(t("errors.noGeolocation"));
      setLocating(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const url = `${API.nominatimBaseUrl}/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`;
          const response = await fetch(url, {
            headers: {
              "User-Agent": API.userAgent,
              "Accept-Language": "en",
            },
          });
          if (!response.ok) {
            throw new Error("Failed to fetch address from Nominatim.");
          }
          const data = await response.json();
          if (data.address && data.address.postcode) {
            const formattedPostcode = formatPostalCode(data.address.postcode);
            setZip(formattedPostcode);
            setError("");
          } else {
            setError(t("errors.noPostcode"));
          }
        } catch (err) {
          setError(t("errors.apiError"));
        }
        setLocating(false);
      },
      (geoError) => {
        switch (geoError.code) {
          case geoError.PERMISSION_DENIED:
            setError(t("errors.permissionDenied"));
            break;
          case geoError.POSITION_UNAVAILABLE:
            setError(t("errors.positionUnavailable"));
            break;
          case geoError.TIMEOUT:
            setError(t("errors.timeout"));
            break;
          default:
            setError(t("errors.defaultLocation"));
        }
        setLocating(false);
      }
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!zip.trim()) {
      setError(t("errors.emptyZip"));
    } else if (!validateZip(zip)) {
      setError(t("errors.invalidZip"));
    } else {
      setError("");
      // Save postal code to sessionStorage for persistence across navigation
      postalCodeHelper.savePostalCode(zip);
      navigate("/search-results", { state: { zip } });
    }
  };

  // Handle video transitions (only if videos are enabled)
  useEffect(() => {
    // Skip video logic if videos are disabled
    if (!videosEnabled) return;

    const currentVideo = showingCurrent ? video1Ref.current : video2Ref.current;
    const nextVideo = showingCurrent ? video2Ref.current : video1Ref.current;

    const handleTimeUpdate = () => {
      if (currentVideo && nextVideo) {
        const timeRemaining = currentVideo.duration - currentVideo.currentTime;

        // Start loading next video 3 seconds before current ends
        if (timeRemaining <= 3 && timeRemaining > 1.5 && nextVideo.paused) {
          const nextIndex = (currentVideoIndex + 1) % VIDEO_URLS.length;
          nextVideo.src = VIDEO_URLS[nextIndex];
          nextVideo.load();
        }

        // Start crossfade 1.5 seconds before video ends
        if (timeRemaining <= 1.5 && timeRemaining > 0) {
          nextVideo.currentTime = 0;
          nextVideo.play().catch(() => {}); // Silently handle autoplay failures
          setShowingCurrent(!showingCurrent);

          const nextIndex = (currentVideoIndex + 1) % VIDEO_URLS.length;
          setCurrentVideoIndex(nextIndex);
          setNextVideoIndex((nextIndex + 1) % VIDEO_URLS.length);
        }
      }
    };

    // Handle when first video is ready to play
    const handleCanPlay = () => {
      setVideoReady(true);
    };

    if (currentVideo) {
      currentVideo.addEventListener("timeupdate", handleTimeUpdate);
      currentVideo.addEventListener("canplay", handleCanPlay);
      return () => {
        currentVideo.removeEventListener("timeupdate", handleTimeUpdate);
        currentVideo.removeEventListener("canplay", handleCanPlay);
      };
    }
  }, [currentVideoIndex, nextVideoIndex, showingCurrent, videosEnabled]);

  // FAQ schema for homepage — shows up as rich snippets in Google SERPs and
  // is lifted verbatim by AI engines (ChatGPT, Perplexity) when users ask
  // about the platform.
  const homepageFaqs = [
    {
      question: t("seo.home.faq.q1.question", "How does CompareMechanic work?"),
      answer: t("seo.home.faq.q1.answer", "Enter your ZIP or postal code to see verified auto repair shops near you. Compare ratings, hourly rates, services, and reviews. Request free quotes from multiple garages, message shops directly, and book appointments online — all in one place."),
    },
    {
      question: t("seo.home.faq.q2.question", "Is CompareMechanic free for customers?"),
      answer: t("seo.home.faq.q2.answer", "Yes. CompareMechanic is completely free for vehicle owners. You can search garages, request quotes, message shops, and book appointments at no cost. We make money from garage subscriptions, not from customers."),
    },
    {
      question: t("seo.home.faq.q3.question", "Which countries does CompareMechanic serve?"),
      answer: t("seo.home.faq.q3.answer", "CompareMechanic operates across the United States, Canada, and Mexico. You can search for verified auto repair shops in any major city and most smaller communities across North America."),
    },
    {
      question: t("seo.home.faq.q4.question", "How do I get a quote from a mechanic?"),
      answer: t("seo.home.faq.q4.answer", "Find a garage on CompareMechanic, click Request Quote, describe what you need, and the shop will respond with an estimate — typically within 24 hours. You can request quotes from multiple shops at once to compare prices before choosing."),
    },
    {
      question: t("seo.home.faq.q5.question", "Are the garages on CompareMechanic verified?"),
      answer: t("seo.home.faq.q5.answer", "Yes. Every garage listed has been verified — we confirm business registration, physical location, and contact details. Reviews are from real customers who have used the shop. Shops that repeatedly fail to respond or receive negative feedback are removed from the platform."),
    },
  ];

  return (
    <main className="home-main">
      {/* SEO Meta Tags + Organization + WebSite + FAQPage schemas */}
      <SEOHelmet
        title={t("seo.home.title", "Compare Auto Repair Quotes Online \u2014 US, Canada & Mexico")}
        description={t("seo.home.description", "Compare free auto repair quotes from multiple verified mechanics. Real reviews, real prices, online booking \u2014 across the US, Canada & Mexico.")}
        path="/"
        keywords={t("seo.home.keywords", "compare auto repair quotes, auto repair price comparison, compare mechanic prices, auto repair quotes online, get mechanic quotes, compare car repair shops, auto repair estimates, mechanic quote comparison")}
        schemas={[
          SEOHelmet.organizationSchema(),
          SEOHelmet.websiteSchema(),
          SEOHelmet.faqSchema(homepageFaqs),
        ]}
      />

      {/* Videos - Only render if enabled (respects reduced motion & slow connections) */}
      {/* Only visible on desktop (768px+) via CSS */}
      {videosEnabled && !videoFailed && (
        <>
          {/* Video 1 */}
          <video
            ref={video1Ref}
            autoPlay
            muted
            playsInline
            preload="auto"
            aria-hidden="true"
            onError={() => setVideoFailed(true)}
            className={`home-video ${showingCurrent ? "active" : "inactive"} ${videoReady ? "loaded" : ""}`}
          >
            <source src={VIDEO_URLS[0]} type="video/mp4" />
          </video>

          {/* Video 2 - for crossfade transitions */}
          <video
            ref={video2Ref}
            muted
            playsInline
            preload="none"
            aria-hidden="true"
            onError={() => setVideoFailed(true)}
            className={`home-video ${showingCurrent ? "inactive" : "active"}`}
          >
            {/* Source set dynamically during crossfade transitions */}
          </video>
        </>
      )}

      {/* Semi-transparent overlay - Only visible on desktop (768px+) via CSS */}
      <div className="home-video-overlay" />

      {/* Search container */}
      <div className="home-search-container">
        <h1 className="home-title">{t("home.title")}</h1>

        <SearchForm
          zip={zip}
          setZip={handleZipChange}
          onSubmit={handleSubmit}
          onUseLocation={handleUseMyLocation}
          locating={locating}
          error={error}
          placeholder={t("home.searchPlaceholder")}
          locationLabel={t("home.useMyLocation")}
          searchLabel={t("common.search")}
        />
      </div>
    </main>
  );
}

export default Home;
